import * as Types from '@aeppic/types'

import { DataValidationErrors, Form } from './form.js'

export type DocumentValidationIssueKind = 'required' | 'cardinality' | 'type' | 'format' | 'missing'

export type DocumentValidationIssue = {
  kind: DocumentValidationIssueKind
  fieldName: string
  fieldLabel: string
  message: string
  expected?: string
  receivedType?: string
  receivedValue?: string
  documentId?: string
  documentVersion?: string
  formId?: string
  formVersion?: string
}

export type DocumentValidationResult = {
  valid: boolean
  issues: DocumentValidationIssue[]
  errors: DataValidationErrors
}

export type ValidateDocumentDataOptions = {
  documentId?: string
  documentVersion?: string
  formId?: string
  formVersion?: string
}

export function validateDocumentAgainstForm(document: Types.Document, form: Form): DocumentValidationResult {
  return validateDocumentDataAgainstForm(document.data || {}, form, {
    documentId: document.id,
    documentVersion: document.v,
    formId: form.id,
    formVersion: form.v,
  })
}

export function validateDocumentDataAgainstForm(data: Types.DocumentData, form: Form, options: ValidateDocumentDataOptions = {}): DocumentValidationResult {
  const errors = form.validateDocumentData(data || {})
  const issues: DocumentValidationIssue[] = []

  for (const field of form.fields) {
    if (data && Object.prototype.hasOwnProperty.call(data, field.name)) {
      continue
    }

    if (!form.allowsUndefinedFields) {
      const label = form.getPlaceholder(field.name)?.label || field.name
      issues.push({
        ...options,
        kind: 'missing',
        fieldName: field.name,
        fieldLabel: label,
        message: `${label} is missing`,
        expected: formatExpectedFieldValue(field),
        receivedType: 'undefined',
        receivedValue: 'undefined',
      })
    }
  }

  if (errors) {
    for (const fieldName of Object.keys(errors)) {
      const fieldErrors = errors[fieldName]
      const field = form.getField(fieldName)
      const label = form.getPlaceholder(fieldName)?.label || fieldName
      const value = data ? data[fieldName] : undefined

      for (const kind of ['required', 'type', 'cardinality', 'format'] as const) {
        const error = fieldErrors[kind]

        if (!error) {
          continue
        }

        issues.push({
          ...options,
          kind,
          fieldName,
          fieldLabel: label,
          message: error.text || `${label} has an invalid value`,
          expected: field ? formatExpectedFieldValue(field) : undefined,
          receivedType: formatValueType(value),
          receivedValue: formatValueForError(value),
        })
        break
      }
    }
  }

  return {
    valid: issues.length === 0,
    issues,
    errors,
  }
}

export function validateDocumentDataAgainstFormDown(data: Types.DocumentData, formDownDefinition: string, options: ValidateDocumentDataOptions = {}): DocumentValidationResult {
  const form = new Form({
    id: options.formId || 'formdown-validation-form',
    v: options.formVersion || 'initial',
    p: 'validation',
    f: { id: 'form', v: 'initial', text: 'Form' },
    locks: [],
    created: { at: '', by: '', from: '', client: '' },
    modified: { at: '', by: '', from: '', client: '' },
    a_depth: 0,
    a: [],
    a_forms: [],
    inheritedLocks: [],
    data: {
      name: 'FormDown validation form',
      definition: formDownDefinition,
    },
  })

  return validateDocumentDataAgainstForm(data, form, {
    ...options,
    formId: form.id,
    formVersion: form.v,
  })
}

function formatExpectedFieldValue(fieldInfo) {
  const cardinality = fieldInfo.cardinality ? `[] ${fieldInfo.cardinality.min}-${fieldInfo.cardinality.max}` : ''
  const subtype = fieldInfo.subType ? `/${fieldInfo.subType}` : ''
  return `${fieldInfo.type}${subtype}${cardinality}`
}

function formatValueType(value) {
  if (Array.isArray(value)) {
    return 'array'
  }

  if (value === null) {
    return 'null'
  }

  return typeof value
}

function formatValueForError(value) {
  let serialized: string

  try {
    serialized = JSON.stringify(value)
  } catch (error) {
    serialized = String(value)
  }

  if (serialized === undefined) {
    serialized = String(value)
  }

  return serialized
}
